import { useCallback, useEffect, useState } from "react";
import { getWorkerDetailedProfile } from "./workerDetailedProfileApi.js";

/**
 * P146: loads one Quick Registered worker's detailed profile for the editor
 * drawer. Mirrors useAgentApplicantDetail's shape ({ status, data, error,
 * refetch }) so the two can sit side by side in the same drawer without two
 * different loading conventions.
 *
 * `applyProfile` exists because a successful save/upload already returns the
 * full refreshed profile - re-fetching it would be a second round trip that
 * could briefly show stale numbers. Callers hand the response straight back
 * in, so the completeness meter, status pills, and audit line update from the
 * same payload the write produced.
 *
 * A refetch preserves the previous `data` rather than blanking it, so the
 * drawer never flashes an empty form mid-read.
 */
export function useWorkerDetailedProfile(applicationId) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!applicationId) {
      setState({ status: "idle", data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));
    getWorkerDetailedProfile(applicationId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);
  const applyProfile = useCallback((profile) => {
    setState({ status: "ready", data: profile, error: null });
  }, []);

  return { ...state, refetch, applyProfile };
}
